import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { AdvisorInvitationVerificationResponse } from '../../models/Advisor';
import { publicAdvisorInvitationService } from '../../services/PublicAdvisorInvitationService';
import { createComprehensiveErrorHandler } from '../../utils/advisorErrorHandling';

export function useAcceptInvitationVerification() {
  const { invitationId } = useParams<{ invitationId: string }>();
  const [verificationData, setVerificationData] =
    useState<AdvisorInvitationVerificationResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isExistingContact, setIsExistingContact] = useState(false);
  const [prefilledFirstName, setPrefilledFirstName] = useState('');
  const [prefilledLastName, setPrefilledLastName] = useState('');

  useEffect(() => {
    const verifyInvitation = async () => {
      if (!invitationId) {
        setError('Invalid invitation link');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const verification =
          await publicAdvisorInvitationService.verifyAdvisorInvitation(invitationId);

        if (!verification.valid) {
          setError(verification.error || 'This invitation is no longer valid');
          return;
        }

        setVerificationData(verification);
        const existingContact = verification.invitation?.existingContact || false;
        setIsExistingContact(existingContact);

        if (!existingContact) {
          setPrefilledFirstName(verification.invitation?.firstName || '');
          setPrefilledLastName(verification.invitation?.lastName || '');
        }
      } catch (verifyError: unknown) {
        const errorHandler = createComprehensiveErrorHandler({
          operation: 'verify',
          invitationId,
        });
        setError(errorHandler(verifyError).message);
      } finally {
        setLoading(false);
      }
    };

    void verifyInvitation();
  }, [invitationId]);

  return {
    invitationId,
    verificationData,
    loading,
    error,
    isExistingContact,
    prefilledFirstName,
    prefilledLastName,
    setError,
  };
}
